import { Directive, ElementRef, Input, NgZone, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Subscription } from 'rxjs';
import { AutoTranslateService } from 'app/services/auto-translate.service';
import { LanguageService, SupportedLanguage } from 'app/services/language.service';

@Directive({
    selector: '[autoTranslate]',
    standalone: true,
})
export class AutoTranslateDirective implements OnInit, OnDestroy {
    @Input() autoTranslate: boolean | string = true;
    @Input() autoTranslateSkip: boolean = false;

    private originalText: string = '';
    private languageSubscription?: Subscription;
    private isTranslating = false;

    constructor(
        private el: ElementRef<HTMLElement>,
        private renderer: Renderer2,
        private translateService: AutoTranslateService,
        private languageService: LanguageService,
        private ngZone: NgZone,
    ) {}

    ngOnInit(): void {
        if (this.autoTranslate === false || this.autoTranslate === 'false') {
            return;
        }

        this.storeOriginalText();

        if (!this.originalText || !this.originalText.trim()) {
            return;
        }

        this.languageSubscription = this.languageService.getLanguage$().subscribe((language) => {
            this.translate(language);
        });

        const currentLang = this.languageService.getCurrentLanguage();
        if (currentLang === 'pt-br') {
            return;
        }

        this.translate(currentLang);
    }

    ngOnDestroy(): void {
        if (this.languageSubscription) {
            this.languageSubscription.unsubscribe();
        }
    }

    private storeOriginalText(): void {
        const element = this.el.nativeElement;
        if (!(element instanceof HTMLElement)) {
            return;
        }

        const stored = element.getAttribute('data-original-text');
        if (stored) {
            this.originalText = stored;
            return;
        }

        if (this.autoTranslateSkip) {
            this.originalText = this.getDirectTextContent(element);
        } else {
            this.originalText = element.textContent || element.innerText || '';
        }

        if (this.originalText) {
            this.renderer.setAttribute(element, 'data-original-text', this.originalText);
        }
    }

    private getDirectTextContent(element: HTMLElement): string {
        let text = '';
        for (const node of Array.from(element.childNodes)) {
            if (node.nodeType === Node.TEXT_NODE) {
                text += node.textContent || '';
            }
        }
        return text.trim();
    }

    private translate(targetLanguage: SupportedLanguage): void {
        if (!this.originalText) {
            return;
        }

        if (targetLanguage === 'pt-br') {
            this.isTranslating = false;
            this.restoreOriginalText();
            return;
        }

        // Permite nova tradução mesmo se a anterior ainda estiver em voo
        this.isTranslating = true;

        this.translateService.translate(this.originalText, targetLanguage).subscribe({
            next: (translated) => {
                // Ignora resposta atrasada se o idioma já mudou de novo
                if (this.languageService.getCurrentLanguage() !== targetLanguage) {
                    return;
                }
                this.ngZone.run(() => {
                    this.updateText(translated);
                    this.isTranslating = false;
                });
            },
            error: (error) => {
                console.warn('Erro ao traduzir elemento:', error);
                this.ngZone.run(() => {
                    this.isTranslating = false;
                });
            },
        });
    }

    private updateText(translated: string): void {
        const element = this.el.nativeElement;

        if (this.autoTranslateSkip) {
            this.replaceDirectTextContent(element, translated);
        } else {
            const hasHtml = element.children.length > 0 || element.innerHTML !== element.textContent;

            if (hasHtml) {
                this.replaceTextInHtml(element, translated);
            } else {
                element.textContent = translated;
            }
        }
    }

    private replaceDirectTextContent(element: HTMLElement, newText: string): void {
        const textNodes: Node[] = [];
        for (const node of Array.from(element.childNodes)) {
            if (node.nodeType === Node.TEXT_NODE) {
                textNodes.push(node);
            }
        }
        textNodes.forEach((node) => this.renderer.removeChild(element, node));

        if (newText) {
            const textNode = this.renderer.createText(newText);
            this.renderer.insertBefore(element, textNode, element.firstChild);
        }
    }

    private replaceTextInHtml(element: HTMLElement, translated: string): void {
        const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);

        const textNodes: Node[] = [];
        let node: Node | null;
        while ((node = walker.nextNode())) {
            if (node.textContent && node.textContent.trim()) {
                textNodes.push(node);
            }
        }

        if (textNodes.length > 0) {
            const firstNode = textNodes[0];
            if (firstNode.parentNode) {
                firstNode.textContent = translated;
                for (let i = 1; i < textNodes.length; i++) {
                    const nodeToRemove = textNodes[i];
                    if (nodeToRemove.parentNode) {
                        this.renderer.removeChild(nodeToRemove.parentNode, nodeToRemove);
                    }
                }
            }
        } else {
            element.textContent = translated;
        }
    }

    private restoreOriginalText(): void {
        this.updateText(this.originalText);
    }
}
